const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export function normalizeRegNumber(value) {
  return String(value || '').replace(/\s/g, '').toUpperCase();
}

/** Plates that should always show as Inactive in fleet / drivers views. */
export const INACTIVE_FLEET_PLATES = new Set(['T663DQF']);

/** Plates that should always show as Active (overrides repair / due-service defaults). */
export const ACTIVE_FLEET_PLATES = new Set(['T993ESE', 'T112ESF']);

export function isInactiveFleetPlate(plate) {
  return INACTIVE_FLEET_PLATES.has(normalizeRegNumber(plate));
}

export function isActiveFleetPlate(plate) {
  return ACTIVE_FLEET_PLATES.has(normalizeRegNumber(plate));
}

export function normalizePersonToken(value) {
  return String(value || '').trim().toLowerCase();
}

function isPlateLike(value) {
  const compact = normalizeRegNumber(value).replace(/-/g, '');
  if (!compact) return false;
  return /^[A-Z]{1,3}\d{2,4}[A-Z]{2,4}$/.test(compact);
}

function sanitizeDriverName(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  // Remove plate-like chunks accidentally pasted into userName.
  const cleaned = raw
    .replace(/\b[A-Z]{1,3}\d{2,4}[A-Z]{2,4}\b/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*-\s*$/, '')
    .trim();

  // Canonical driver naming fixes from live Car Rental rows.
  if (/^onyango\s+cromwell\s+oronje$/i.test(cleaned)) {
    return 'ONYANGO Cromwell';
  }
  // Keep Onesmo distinct from Deogratius Munishi (shared given name).
  if (/^(onesmo\s+deogratius|deogratius\s+onesmo)$/i.test(cleaned)) {
    return 'DEOGRATIUS Onesmo';
  }
  if (/^deogratius\s+munishi$/i.test(cleaned)) {
    return 'Deogratius Munishi';
  }

  return cleaned;
}

/**
 * Distinct people who share a given name (or similar tokens) must never merge.
 * Returns a stable id when the name clearly identifies one of these drivers.
 */
function distinctDriverIdentity(name) {
  const n = normalizePersonToken(name);
  if (!n) return null;
  if (/\bonesmo\b/.test(n)) return 'deogratius-onesmo';
  if (/\bmunishi\b/.test(n)) return 'deogratius-munishi';
  return null;
}

function normalizeCarRentalRow(row) {
  const regFromReg = normalizeRegNumber(row?.regNumber);
  const regFromUser = normalizeRegNumber(row?.userName);
  const userLooksPlate = isPlateLike(row?.userName);
  const regLooksPlate = isPlateLike(row?.regNumber);

  // Handle swapped fields: userName contains plate, regNumber contains person name.
  if (userLooksPlate && !regLooksPlate) {
    return {
      ...row,
      regNumber: regFromUser || row?.userName || '',
      userName: sanitizeDriverName(row?.regNumber)
    };
  }

  return {
    ...row,
    regNumber: regFromReg || '',
    userName: sanitizeDriverName(row?.userName)
  };
}

/** Match fleet driver name (e.g. Geoffrey) to Car Rental Invoicing user name. */
export function driverNameMatches(fleetDriver, carRentalUserName) {
  const fleet = normalizePersonToken(fleetDriver);
  const rental = normalizePersonToken(carRentalUserName);
  if (!fleet || !rental) return false;

  // Never merge clearly distinct people (Onesmo ≠ Munishi).
  const fleetId = distinctDriverIdentity(fleet);
  const rentalId = distinctDriverIdentity(rental);
  if (fleetId && rentalId && fleetId !== rentalId) return false;

  if (fleet === rental) return true;

  const fleetParts = fleet.split(/\s+/).filter(Boolean);
  const rentalParts = rental.split(/\s+/).filter(Boolean);

  // Same tokens in any order (e.g. "Onesmo Deogratius" ↔ "DEOGRATIUS Onesmo")
  if (
    fleetParts.length > 1 &&
    fleetParts.length === rentalParts.length &&
    fleetParts.every((p) => rentalParts.includes(p))
  ) {
    return true;
  }

  // Single-token side (driverKey / first name only): token must appear in the other name.
  // Still respect distinct-identity block above (e.g. "deogratius" alone won't pass
  // when the other side is already identified as Onesmo vs Munishi — only when ONE side
  // has an identity. If only one side has identity, require the token to match that person.)
  if (fleetParts.length === 1) {
    if (rentalId && fleetParts[0] === 'deogratius') return false;
    return rentalParts.includes(fleetParts[0]) || rental.includes(fleet);
  }
  if (rentalParts.length === 1) {
    if (fleetId && rentalParts[0] === 'deogratius') return false;
    return fleetParts.includes(rentalParts[0]) || fleet.includes(rental);
  }

  // Both multi-part: do not match on a shared given name alone when other names differ
  // (e.g. "DEOGRATIUS Onesmo" must not match "Deogratius Munishi").
  const shared = fleetParts.filter((p) => p.length >= 3 && rentalParts.includes(p));
  if (shared.length >= 2) return true;
  if (shared.length === 1) {
    const sharedToken = shared[0];
    const fleetRest = fleetParts.filter((p) => p !== sharedToken);
    const rentalRest = rentalParts.filter((p) => p !== sharedToken);
    if (fleetRest.length && rentalRest.length) {
      return fleetRest.some((p) => rentalRest.includes(p));
    }
    return true;
  }

  return false;
}

export function formatRentalMonth(year, month) {
  const label = MONTH_NAMES[Math.max(0, Math.min(11, Number(month) - 1))] || String(month);
  return `${label} ${year}`;
}

/** Calendar month used for “present month” fleet active / inactive rules. */
export function getCurrentRentalPeriod() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

export function carRentalRowInPeriod(row, year, month) {
  return Number(row?.year) === Number(year) && Number(row?.month) === Number(month);
}

export function hasCarRentalInPeriod(rows, year, month) {
  return (rows || []).some((row) => carRentalRowInPeriod(row, year, month));
}

/** True when this driver has at least one Car Rental row in the given month. */
export function driverHasCarRentalInPeriod(driverName, carRentalRows, year, month) {
  const pool = getCarRentalRowsForDriver(driverName, carRentalRows);
  return hasCarRentalInPeriod(pool, year, month);
}

export function sortCarRentalRowsByPeriodDesc(rows) {
  return [...(rows || [])].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });
}

/**
 * Known plate ownership for drivers who share a given name.
 * Prevents a wrong userName on a Car Rental row from attaching the other person's car.
 */
const PLATE_DRIVER_IDENTITY = {
  T141DQL: 'deogratius-munishi',
  T395DXD: 'deogratius-onesmo',
  T405ELG: 'deogratius-onesmo'
};

/** All Car Rental Invoicing rows for a driver (every month). */
export function getCarRentalRowsForDriver(driverName, carRentalRows, options = {}) {
  const plate = options.plate ? normalizeRegNumber(options.plate) : null;
  const driverId = distinctDriverIdentity(driverName);
  return (carRentalRows || [])
    .map(normalizeCarRentalRow)
    .filter((row) => {
      if (!driverNameMatches(driverName, row.userName)) return false;
      if (plate && normalizeRegNumber(row.regNumber) !== plate) return false;
      const rowPlateId = PLATE_DRIVER_IDENTITY[normalizeRegNumber(row.regNumber)];
      if (driverId && rowPlateId && driverId !== rowPlateId) return false;
      return true;
    });
}

/** Latest assignment across all months and vehicles for this driver. */
export function getLatestCarRentalAssignment(driverName, carRentalRows) {
  const pool = getCarRentalRowsForDriver(driverName, carRentalRows);
  if (!pool.length) return null;
  return sortCarRentalRowsByPeriodDesc(pool)[0];
}

/** Latest month row for a specific reg number (scans all periods). */
export function getLatestCarRentalAssignmentForReg(driverName, regNumber, carRentalRows) {
  const pool = getCarRentalRowsForDriver(driverName, carRentalRows, { plate: regNumber });
  if (!pool.length) return null;
  return sortCarRentalRowsByPeriodDesc(pool)[0];
}

/** Distinct vehicles a driver has used, with every month from Car Rental Invoicing. */
export function buildDriverVehicleHistory(driverName, carRentalRows) {
  const rows = getCarRentalRowsForDriver(driverName, carRentalRows);
  const byReg = new Map();

  rows.forEach((row) => {
    const reg = normalizeRegNumber(row.regNumber) || '—';
    if (!byReg.has(reg)) {
      byReg.set(reg, {
        regNumber: row.regNumber || reg,
        months: []
      });
    }

    const entry = byReg.get(reg);
    const monthKey = `${row.year}-${String(row.month).padStart(2, '0')}`;
    const monthLabel = formatRentalMonth(row.year, row.month);

    const existing = entry.months.find((m) => m.key === monthKey);
    if (!existing) {
      entry.months.push({
        key: monthKey,
        year: row.year,
        month: row.month,
        label: monthLabel,
        project: row.project || '',
        rentalCompany: row.rentalCompany || '',
        userName: row.userName || ''
      });
    }
  });

  return Array.from(byReg.values())
    .map((entry) => {
      const months = entry.months.sort((a, b) => b.key.localeCompare(a.key));
      const latest = months[0];
      return {
        regNumber: entry.regNumber,
        project: latest?.project || '',
        rentalCompany: latest?.rentalCompany || '',
        userName: latest?.userName || '',
        latestMonth: latest?.label || '',
        months
      };
    })
    .sort((a, b) => String(a.regNumber).localeCompare(String(b.regNumber), undefined, { sensitivity: 'base' }));
}

export function mergeDriverProfileWithCarRental(baseProfile, vehicle, carRentalRows) {
  if (!baseProfile || !vehicle) return baseProfile;

  // Prefer the full rental display name when available so Onesmo/Munishi stay distinct.
  const driverLookupName =
    [baseProfile.driver, vehicle.driver].find((n) =>
      /\b(onesmo|munishi)\b/i.test(String(n || ''))
    ) ||
    baseProfile.driver ||
    vehicle.driver;

  const driverRows = getCarRentalRowsForDriver(driverLookupName, carRentalRows);
  const latestOverall = getLatestCarRentalAssignment(driverLookupName, carRentalRows);
  const latestForVehicle = getLatestCarRentalAssignmentForReg(
    driverLookupName,
    vehicle.plate,
    carRentalRows
  );
  const vehicleHistory = buildDriverVehicleHistory(driverLookupName, carRentalRows);

  const periodCount = new Set(driverRows.map((r) => `${r.year}-${r.month}`)).size;

  if (!latestOverall) {
    return {
      ...baseProfile,
      regNumber: vehicle.plate,
      rentalCompany: vehicle.ownerRentalCompany,
      rentalSource: null,
      rentalPeriodCount: 0,
      vehicleHistory,
      vehicleProject: vehicle.project,
      vehicleRegNumber: vehicle.plate,
      vehicleRentalCompany: vehicle.ownerRentalCompany,
      vehicleRentalMonth: null
    };
  }

  const currentReg = normalizeRegNumber(latestOverall.regNumber);
  const fleetReg = normalizeRegNumber(vehicle.plate);
  const switchedVehicle = currentReg && fleetReg && currentReg !== fleetReg;

  return {
    ...baseProfile,
    project: latestOverall.project || baseProfile.project,
    driver: latestOverall.userName || baseProfile.driver,
    regNumber: latestOverall.regNumber || vehicle.plate,
    rentalCompany: latestOverall.rentalCompany || vehicle.ownerRentalCompany,
    rentalMonth: formatRentalMonth(latestOverall.year, latestOverall.month),
    rentalSource: 'car-rental-invoicing',
    rentalPeriodCount: periodCount,
    switchedVehicle,
    latestAssignmentReg: latestOverall.regNumber || '',
    vehicleHistory,
    vehicleProject: latestForVehicle?.project || latestOverall.project || vehicle.project,
    vehicleRegNumber: latestForVehicle?.regNumber || vehicle.plate,
    vehicleRentalCompany:
      latestForVehicle?.rentalCompany || latestOverall.rentalCompany || vehicle.ownerRentalCompany,
    vehicleRentalMonth: latestForVehicle
      ? formatRentalMonth(latestForVehicle.year, latestForVehicle.month)
      : formatRentalMonth(latestOverall.year, latestOverall.month)
  };
}

/**
 * Build driver vehicle list from all Car Rental Invoicing rows (every month).
 * Merges with fleet vehicles when reg number matches.
 */
export function buildDriverVehicleListFromCarRentals(carRentalRows, fleetVehicles = []) {
  const { year: currentYear, month: currentMonth } = getCurrentRentalPeriod();
  const byReg = new Map();

  (carRentalRows || []).forEach((rawRow) => {
    const row = normalizeCarRentalRow(rawRow);
    const reg = normalizeRegNumber(row.regNumber);
    if (!reg) return;
    if (!byReg.has(reg)) byReg.set(reg, []);
    byReg.get(reg).push(row);
  });

  const fleetByPlate = new Map(
    fleetVehicles.map((v) => [normalizeRegNumber(v.plate), v])
  );

  const fromRentals = Array.from(byReg.entries()).map(([reg, rows]) => {
    const latest = sortCarRentalRowsByPeriodDesc(rows)[0];
    const fleetMatch = fleetByPlate.get(reg);
    const normalizedDriverName = sanitizeDriverName(latest.userName);
    const assignedThisMonth = hasCarRentalInPeriod(rows, currentYear, currentMonth);

    const resolveStatus = (plateValue, baseStatus, baseDisplayStatus) => {
      if (!assignedThisMonth) {
        return { status: 'inactive', displayStatus: 'inactive' };
      }
      if (isInactiveFleetPlate(plateValue)) {
        return { status: 'inactive', displayStatus: 'inactive' };
      }
      if (isActiveFleetPlate(plateValue)) {
        return { status: 'active', displayStatus: 'active' };
      }
      return {
        status: baseStatus || 'active',
        displayStatus: baseDisplayStatus || baseStatus || 'active'
      };
    };

    if (fleetMatch) {
      const driverName =
        normalizedDriverName && !isPlateLike(normalizedDriverName)
          ? normalizedDriverName
          : fleetMatch.driver;
      const firstName = driverName.split(/\s+/)[0] || driverName;
      const plateValue = latest.regNumber || fleetMatch.plate || reg;
      const { status, displayStatus } = resolveStatus(
        plateValue,
        fleetMatch.status,
        fleetMatch.displayStatus
      );
      return {
        ...fleetMatch,
        plate: latest.regNumber || fleetMatch.plate,
        driver: driverName,
        driverFirstName: firstName,
        project: latest.project || fleetMatch.project,
        ownerRentalCompany: latest.rentalCompany || fleetMatch.ownerRentalCompany,
        rentalSource: 'car-rental-invoicing',
        fleetDataId: fleetMatch.id,
        status,
        displayStatus
      };
    }

    const driverName = normalizedDriverName && !isPlateLike(normalizedDriverName) ? normalizedDriverName : '—';
    const firstName = driverName.split(/\s+/)[0] || driverName;
    const plateValue = latest.regNumber || reg;
    const { status, displayStatus } = resolveStatus(plateValue, 'active', 'active');
    return {
      id: `cri-${reg}`,
      plate: latest.regNumber || reg,
      model: 'Rental vehicle',
      driver: driverName,
      driverFirstName: firstName,
      project: latest.project || '—',
      ownerRentalCompany: latest.rentalCompany || '—',
      odometer: 0,
      status,
      displayStatus,
      lastServiceDate: '',
      lastServiceOdometer: 0,
      insuranceExpiry: '',
      roadLicenceExpiry: '',
      phone: '',
      email: '',
      driverAvatar: '/assets/driver-avatars/default.svg',
      totalCost: 0,
      costPerKm: 0,
      nextServiceDue: {
        nextKm: 5000,
        kmRemaining: 5000,
        isDue: false,
        isSoon: false
      },
      rentalSource: 'car-rental-invoicing',
      fleetDataId: null
    };
  });

  if (fromRentals.length) {
    return fromRentals.sort((a, b) =>
      String(a.plate).localeCompare(String(b.plate), undefined, { sensitivity: 'base' })
    );
  }

  return [...fleetVehicles];
}

/** Resolve fleet mock-data id from a driver vehicle (by id or matching plate). */
export function resolveFleetDataVehicleId(vehicle, fleetVehicles = []) {
  if (!vehicle) return null;
  if (vehicle.fleetDataId) return vehicle.fleetDataId;
  if (fleetVehicles.some((v) => v.id === vehicle.id)) return vehicle.id;
  const plate = normalizeRegNumber(vehicle.plate);
  const exact = fleetVehicles.find((v) => normalizeRegNumber(v.plate) === plate);
  if (exact) return exact.id;
  // Tolerate one-letter plate typos between similar regs.
  if (plate.length >= 6) {
    const fuzzy = fleetVehicles.find((v) => {
      const other = normalizeRegNumber(v.plate);
      if (other.length !== plate.length) return false;
      let diffs = 0;
      for (let i = 0; i < plate.length; i += 1) {
        if (plate[i] !== other[i]) diffs += 1;
        if (diffs > 1) return false;
      }
      return diffs === 1;
    });
    if (fuzzy) return fuzzy.id;
  }
  return null;
}
